import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { ShoppingCart, Plus, X, Trash2, Check, Ban, FileText, Building2, Truck } from "lucide-react";
import { rank } from "../../lib/rbac";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const statusStyle = (s) => s === "Approved" ? "bg-accent_success/15 text-accent_success border-accent_success/30"
  : s === "Rejected" ? "bg-accent_danger/15 text-accent_danger border-accent_danger/30"
  : "bg-primary/15 text-gold border-primary/30";

export default function Procurement() {
  const [tab, setTab] = useState("orders");
  const [user, setUser] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [orders, setOrders] = useState([]);
  const [branches, setBranches] = useState([]);
  const [showVendor, setShowVendor] = useState(false);
  const [showPO, setShowPO] = useState(false);
  const [vendorForm, setVendorForm] = useState({ name: "", contact: "", gstin: "", category: "General", branch: "" });
  const [poForm, setPoForm] = useState({ vendor_name: "", items: [{ item: "", qty: 1, rate: 0 }], notes: "", quotation_file: "", branch: "" });
  const [uploading, setUploading] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [v, o, me, br] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/vendors`),
        axios.get(`${BACKEND_URL}/api/purchase-orders`),
        axios.get(`${BACKEND_URL}/api/auth/me`),
        axios.get(`${BACKEND_URL}/api/branches`),
      ]);
      setVendors(v.data); setOrders(o.data); setUser(me.data.user); setBranches(br.data);
    } catch (e) { console.error(e); }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const canManage = user && rank(user.role) >= 60;      // BM+
  const canApprove = user && rank(user.role) >= 80;     // GM+
  const isSenior = user && rank(user.role) >= 80;       // can choose a branch

  const addVendor = async (e) => {
    e.preventDefault();
    try { await axios.post(`${BACKEND_URL}/api/vendors`, vendorForm); setShowVendor(false); setVendorForm({ name: "", contact: "", gstin: "", category: "General", branch: "" }); fetchAll(); }
    catch (err) { alert(err.response?.data?.detail || "Not allowed"); }
  };
  const delVendor = async (id) => { try { await axios.delete(`${BACKEND_URL}/api/vendors/${id}`); fetchAll(); } catch (e) { alert(e.response?.data?.detail || "Not allowed"); } };

  const uploadQuote = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try { const fd = new FormData(); fd.append("file", file); const r = await axios.post(`${BACKEND_URL}/api/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } }); setPoForm(p => ({ ...p, quotation_file: r.data.path })); }
    catch (err) { alert("Upload failed"); }
    setUploading(false);
  };

  const setItem = (i, k, v) => setPoForm(p => ({ ...p, items: p.items.map((it, idx) => idx === i ? { ...it, [k]: v } : it) }));
  const poTotal = poForm.items.reduce((s, it) => s + (parseFloat(it.qty || 0) * parseFloat(it.rate || 0)), 0);

  const createPO = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BACKEND_URL}/api/purchase-orders`, { ...poForm, items: poForm.items.map(it => ({ item: it.item, qty: parseFloat(it.qty || 0), rate: parseFloat(it.rate || 0) })) });
      setShowPO(false); setPoForm({ vendor_name: "", items: [{ item: "", qty: 1, rate: 0 }], notes: "", quotation_file: "", branch: "" }); fetchAll();
    } catch (err) { alert(err.response?.data?.detail || "Not allowed"); }
  };

  const decide = async (po, decision) => {
    const note = decision === "Rejected" ? (window.prompt("Reason for rejection (optional):") || "") : (window.prompt("Approval note (optional):") || "");
    try { await axios.post(`${BACKEND_URL}/api/purchase-orders/${po.id}/decision`, { decision, note }); fetchAll(); }
    catch (err) { alert(err.response?.data?.detail || "Not allowed"); }
  };

  const inp = "w-full bg-card_bg border border-border_soft rounded-xl p-3 text-text_primary text-sm";

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-8 duration-700" data-testid="procurement-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-4xl font-serif text-text_primary mb-3">Procurement</h1>
          <p className="text-text_secondary text-sm">Manage vendors and raise purchase orders. Orders need GM/MD/CEO approval.</p>
        </div>
        <div className="flex gap-2">
          {tab === "vendors" && canManage && <button data-testid="add-vendor-open" onClick={() => setShowVendor(true)} className="bg-primary text-[#111827] px-5 py-3 rounded-xl font-bold flex items-center gap-2 hover:bg-primary_hover transition-colors"><Plus size={18} /> Add Vendor</button>}
          {tab === "orders" && canManage && <button data-testid="add-po-open" onClick={() => setShowPO(true)} className="bg-primary text-[#111827] px-5 py-3 rounded-xl font-bold flex items-center gap-2 hover:bg-primary_hover transition-colors"><Plus size={18} /> New Purchase Order</button>}
        </div>
      </div>

      <div className="flex gap-2">
        <button data-testid="proc-tab-orders" onClick={() => setTab("orders")} className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors ${tab === "orders" ? "bg-primary text-[#111827]" : "bg-card_bg border border-border_soft text-text_secondary"}`}>Purchase Orders</button>
        <button data-testid="proc-tab-vendors" onClick={() => setTab("vendors")} className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors ${tab === "vendors" ? "bg-primary text-[#111827]" : "bg-card_bg border border-border_soft text-text_secondary"}`}>Vendors</button>
      </div>

      {tab === "orders" && (
        <div className="space-y-4">
          {orders.length === 0 && <p className="text-text_secondary text-sm">No purchase orders yet.</p>}
          {orders.map((po) => (
            <div key={po.id} data-testid={`po-${po.po_number}`} className="bg-card_bg border border-border_soft rounded-2xl p-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <span className="font-mono text-gold font-bold">{po.po_number}</span>
                    <span className={`px-3 py-1 rounded-lg text-[10px] font-bold uppercase tracking-widest border ${statusStyle(po.status)}`}>{po.status}</span>
                  </div>
                  <p className="text-text_primary font-semibold flex items-center gap-1.5"><Truck size={14} className="text-text_secondary" /> {po.vendor_name}</p>
                  <p className="text-xs text-text_secondary mt-0.5">By {po.created_by} · {po.branch} · {(po.created_at || "").slice(0, 10)}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-text_primary">₹{(po.total || 0).toLocaleString()}</p>
                  {po.quotation_file && <a href={`${BACKEND_URL}/api/files/${po.quotation_file}`} target="_blank" rel="noreferrer" className="text-xs text-gold flex items-center gap-1 justify-end mt-1 hover:underline"><FileText size={12} /> Quotation</a>}
                </div>
              </div>
              <div className="mt-4 border-t border-border_soft/40 pt-3 space-y-1">
                {(po.items || []).map((it, i) => (
                  <div key={i} className="flex justify-between text-sm text-text_secondary"><span>{it.item} × {it.qty}</span><span>₹{(it.qty * it.rate).toLocaleString()}</span></div>
                ))}
              </div>
              {po.notes && <p className="text-xs text-text_secondary mt-3 italic">Note: {po.notes}</p>}
              {po.decision_by && <p className="text-xs text-text_secondary mt-2">{po.status} by {po.decision_by}{po.decision_note ? ` — ${po.decision_note}` : ""}</p>}
              {canApprove && po.status === "Pending Approval" && (
                <div className="flex gap-2 mt-4">
                  <button data-testid={`approve-${po.po_number}`} onClick={() => decide(po, "Approved")} className="bg-accent_success/15 text-accent_success border border-accent_success/40 px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-1.5 hover:bg-accent_success hover:text-[#111827] transition-colors"><Check size={15} /> Approve</button>
                  <button data-testid={`reject-${po.po_number}`} onClick={() => decide(po, "Rejected")} className="bg-accent_danger/15 text-accent_danger border border-accent_danger/40 px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-1.5 hover:bg-accent_danger hover:text-white transition-colors"><Ban size={15} /> Reject</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "vendors" && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {vendors.length === 0 && <p className="text-text_secondary text-sm col-span-full">No vendors yet.</p>}
          {vendors.map((v) => (
            <div key={v.id} className="bg-card_bg border border-border_soft rounded-2xl p-5 relative">
              {canManage && <button onClick={() => delVendor(v.id)} className="absolute top-4 right-4 text-text_secondary hover:text-accent_danger"><Trash2 size={16} /></button>}
              <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center text-gold border border-primary/20 mb-3"><Building2 size={20} /></div>
              <h3 className="text-lg font-bold text-text_primary">{v.name}</h3>
              <p className="text-xs text-text_secondary mt-1">{v.category}</p>
              {v.contact && <p className="text-sm text-text_secondary mt-2">📞 {v.contact}</p>}
              {v.gstin && <p className="text-xs text-text_secondary font-mono mt-1">GSTIN: {v.gstin}</p>}
            </div>
          ))}
        </div>
      )}

      {showVendor && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-card_bg border border-border_soft p-8 rounded-2xl w-full max-w-md relative shadow-2xl">
            <button onClick={() => setShowVendor(false)} className="absolute top-6 right-6 text-text_secondary hover:text-text_primary"><X size={20} /></button>
            <h2 className="text-2xl font-serif text-text_primary mb-6">Add Vendor</h2>
            <form onSubmit={addVendor} className="space-y-4">
              <input required data-testid="vendor-name" placeholder="Vendor name" value={vendorForm.name} onChange={e => setVendorForm({ ...vendorForm, name: e.target.value })} className={inp} />
              <input placeholder="Contact (phone/email)" value={vendorForm.contact} onChange={e => setVendorForm({ ...vendorForm, contact: e.target.value })} className={inp} />
              <input placeholder="GSTIN" value={vendorForm.gstin} onChange={e => setVendorForm({ ...vendorForm, gstin: e.target.value })} className={inp} />
              <input placeholder="Category (e.g. Equipment, Supplies)" value={vendorForm.category} onChange={e => setVendorForm({ ...vendorForm, category: e.target.value })} className={inp} />
              {isSenior && (
                <select data-testid="vendor-branch" value={vendorForm.branch} onChange={e => setVendorForm({ ...vendorForm, branch: e.target.value })} className={inp}>
                  <option value="">Select branch...</option>
                  {branches.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
                </select>
              )}
              <button type="submit" data-testid="save-vendor-btn" className="w-full bg-primary text-[#111827] font-bold py-3.5 rounded-xl hover:bg-primary_hover transition-colors">Save Vendor</button>
            </form>
          </div>
        </div>
      )}

      {showPO && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-card_bg border border-border_soft p-8 rounded-2xl w-full max-w-lg relative shadow-2xl max-h-[92vh] overflow-y-auto">
            <button onClick={() => setShowPO(false)} className="absolute top-6 right-6 text-text_secondary hover:text-text_primary"><X size={20} /></button>
            <h2 className="text-2xl font-serif text-text_primary mb-6">New Purchase Order</h2>
            <form onSubmit={createPO} className="space-y-4">
              <div>
                <label className="text-xs text-text_secondary mb-1 block">Vendor</label>
                <select required data-testid="po-vendor" value={poForm.vendor_name} onChange={e => setPoForm({ ...poForm, vendor_name: e.target.value })} className={inp}>
                  <option value="">Select vendor...</option>
                  {vendors.map(v => <option key={v.id} value={v.name}>{v.name}</option>)}
                </select>
              </div>
              <div className="space-y-3">
                <label className="text-xs text-text_secondary block">Items</label>
                {poForm.items.map((it, i) => (
                  <div key={i} className="bg-main_bg border border-border_soft/60 rounded-xl p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <input required placeholder="Item name" value={it.item} onChange={e => setItem(i, "item", e.target.value)} className={`${inp} flex-1`} data-testid={`po-item-${i}`} />
                      {poForm.items.length > 1 && <button type="button" onClick={() => setPoForm(p => ({ ...p, items: p.items.filter((_, idx) => idx !== i) }))} className="text-text_secondary hover:text-accent_danger shrink-0"><Trash2 size={16} /></button>}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[10px] text-text_secondary uppercase tracking-wider">Qty</label>
                        <input required type="number" min="0" placeholder="Qty" value={it.qty} onChange={e => setItem(i, "qty", e.target.value)} className={inp} />
                      </div>
                      <div>
                        <label className="text-[10px] text-text_secondary uppercase tracking-wider">Rate (₹)</label>
                        <input required type="number" min="0" placeholder="Rate" value={it.rate} onChange={e => setItem(i, "rate", e.target.value)} className={inp} />
                      </div>
                    </div>
                  </div>
                ))}
                <button type="button" data-testid="po-add-item" onClick={() => setPoForm(p => ({ ...p, items: [...p.items, { item: "", qty: 1, rate: 0 }] }))} className="text-gold text-sm font-semibold flex items-center gap-1"><Plus size={14} /> Add item</button>
              </div>
              <div className="flex justify-between items-center text-text_primary font-bold border-t border-border_soft/40 pt-3"><span>Total</span><span>₹{poTotal.toLocaleString()}</span></div>
              <div>
                <label className="text-xs text-text_secondary mb-1 block">Quotation (image / PDF)</label>
                <input type="file" accept="image/*,application/pdf" onChange={uploadQuote} data-testid="po-quote-upload" className="w-full text-xs text-text_secondary file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-primary file:text-[#111827] file:font-bold file:cursor-pointer" />
                {uploading && <p className="text-xs text-gold mt-1">Uploading...</p>}
                {poForm.quotation_file && !uploading && <p className="text-xs text-accent_success mt-1">✓ Quotation attached</p>}
              </div>
              <textarea placeholder="Notes (optional)" value={poForm.notes} onChange={e => setPoForm({ ...poForm, notes: e.target.value })} className={inp} rows={2} />
              {isSenior && (
                <div>
                  <label className="text-xs text-text_secondary mb-1 block">Branch</label>
                  <select data-testid="po-branch" value={poForm.branch} onChange={e => setPoForm({ ...poForm, branch: e.target.value })} className={inp}>
                    <option value="">Select branch...</option>
                    {branches.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
                  </select>
                </div>
              )}
              <button type="submit" data-testid="save-po-btn" className="w-full bg-primary text-[#111827] font-bold py-3.5 rounded-xl hover:bg-primary_hover transition-colors">Submit for Approval</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
